import { InputMask } from "primereact/inputmask"
import { InputText } from "primereact/inputtext"
import { classNames } from "primereact/utils"
import React, { useCallback, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner } from "@fortawesome/pro-light-svg-icons"
import { ErrorMessage, Field, FieldProps, getIn, useFormikContext } from "formik"

import { emailValidation } from "utils"
import { useCheckEmailExists } from "hooks"

const TelecomContainer: React.FC<IProps> = ({
  parentFieldName,
  checkEmailExists,
  patientOrganization,
  isPhoneOptional,
  showPhoneOptionalCaption,
  inputClassName,
}) => {
  const parentFieldFullName = parentFieldName ? parentFieldName + "." : ""
  return (
    <div className="flex flex-col space-y-2">
      <EmailFields
        checkEmailExists={checkEmailExists}
        patientOrganization={patientOrganization}
        field={`${parentFieldFullName}emails[0].value`}
        inputClassName={inputClassName}
      />
      <PhoneFields
        field={`${parentFieldFullName}phones[0].value`}
        isPhoneOptional={isPhoneOptional}
        showPhoneOptionalCaption={showPhoneOptionalCaption}
        inputClassName={inputClassName}
      />
    </div>
  )
}

const EXIST_MAIL_ERROR = "This email address is in use"

const EmailFields: React.FC<ITelecomFieldProps & IEmailProps> = ({
  field,
  checkEmailExists: checkEmail,
  patientOrganization,
  inputClassName,
}) => {
  const [exists, setExists] = useState(false)
  const prevValueRef = useRef("")
  const { checkEmailExists, isCheckingEmail } = useCheckEmailExists(setExists)
  const { errors } = useFormikContext()
  const fieldError = useMemo(() => getIn(errors, field), [errors, field])

  const validateField = useCallback(
    async (value: string) => {
      const { isValid, msg } = emailValidation(value)
      if (!isValid) {
        prevValueRef.current = value
        return msg
      }

      if (checkEmail && prevValueRef.current !== value) {
        prevValueRef.current = value
        const existsMail = await checkEmailExists({ emailToCheck: value, patientOrganization })
        if (existsMail) return EXIST_MAIL_ERROR
        return undefined
      }

      return String(fieldError).localeCompare(EXIST_MAIL_ERROR) === 0 ? fieldError : undefined
    },
    [checkEmail, checkEmailExists, fieldError, patientOrganization],
  )

  return (
    <Field name={field} validate={validateField}>
      {({
        field: { name, value, onChange, onBlur: fieldOnBlur },
        meta: { touched, error },
        form: { setFieldTouched, validateField: runFieldValidation },
      }: FieldProps) => (
        <div className="flex flex-col relative">
          <div className="field w-full space-y-2">
            <label htmlFor={name} className="font-medium text-gray-500 text-sm mb-0">
              Email*
            </label>
            <InputText
              id={name}
              name={name}
              autoComplete="off"
              aria-autocomplete="none"
              type="email"
              className={classNames("w-full p-inputtext-sm h-10", inputClassName, {
                "p-invalid": touched && error,
              })}
              value={value}
              onChange={(e) => {
                setExists(false)
                onChange(e)
              }}
              onBlur={async (e) => {
                fieldOnBlur(e)
                setFieldTouched(field, true, false)
                await runFieldValidation(field)
              }}
            />
          </div>
          <div className="field-error-spacing">
            {isCheckingEmail && touched && (
              <small className="block">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                <span>Checking email availability...</span>
              </small>
            )}
            {!isCheckingEmail && touched && error && (
              <small className="p-error block">
                <ErrorMessage name={field}>{(msg) => <span className="mr-2">{msg}</span>}</ErrorMessage>
                {exists && patientOrganization && (
                  <a
                    className="text-xs text-blue-500 underline ml-1 cursor-pointer"
                    target="_blank"
                    href={window.VITE_APP_PATIENT_PORTAL_URL}
                    rel="noreferrer"
                  >
                    Already registered? Click here to Login
                  </a>
                )}
              </small>
            )}
          </div>
        </div>
      )}
    </Field>
  )
}

const PhoneFields: React.FC<ITelecomFieldProps & IPhoneProps> = ({
  field,
  isPhoneOptional,
  showPhoneOptionalCaption,
  inputClassName,
}) => {
  const validateField = async (value: string | undefined) => {
    if (!value?.trim()?.length) return isPhoneOptional ? undefined : "Phone number is required"
    if (!/^\d+$/.test(value)) return "Invalid phone number"

    const digits = value.length == 11 && value.startsWith("1") ? value.substring(1) : value

    if (digits.length !== 10) return "Invalid phone number length"

    const areaCode = parseInt(digits.substring(0, 3))
    if (areaCode < 200 || areaCode == 555) return "Invalid phone number area code"

    const exchangeCode = parseInt(digits.substring(3, 6))
    if (exchangeCode < 200) return "Invalid phone number exchange code"
    return undefined
  }

  return (
    <Field name={field} validate={validateField}>
      {({ field: { name, value, onChange }, meta: { touched, error } }: FieldProps) => (
        <div className="flex items-center h-full relative">
          <div className="field w-full space-y-2">
            <label htmlFor={name} className="font-medium text-gray-500 text-sm">
              Phone{isPhoneOptional ? "" : "*"}
              {showPhoneOptionalCaption && isPhoneOptional && (
                <span className="font-normal text-gray-400"> (Optional)</span>
              )}
            </label>
            <InputMask
              id={name}
              name={name}
              type="tel"
              mask="+1 (999) 999-9999"
              unmask={true}
              className={classNames("w-full p-inputtext-sm", inputClassName, {
                "p-invalid": touched && error,
              })}
              value={value}
              onChange={onChange}
            />
          </div>
          <div className="p-error block absolute -bottom-3">
            <ErrorMessage name={field}>{(msg) => <small>{msg}</small>}</ErrorMessage>
          </div>
        </div>
      )}
    </Field>
  )
}

type ITelecomFieldProps = {
  field: string
  inputClassName?: string
}

type IPhoneProps = {
  isPhoneOptional?: boolean
  showPhoneOptionalCaption?: boolean
}

type IEmailProps = {
  checkEmailExists?: boolean
  patientOrganization?: { id: string; name: string }
}

type IProps = {
  parentFieldName?: string
  checkEmailExists?: boolean
  patientOrganization?: { id: string; name: string }
  isPhoneOptional?: boolean
  showPhoneOptionalCaption?: boolean
  inputClassName?: string
}

export { TelecomContainer }
